import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users, Store, FileText, ShoppingCart, TrendingUp,
  Loader2, ArrowUpRight, CreditCard
} from 'lucide-react';
import axios from 'axios';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const sadApi = () => {
  const token = localStorage.getItem('sad_token');
  return axios.create({ baseURL: `${API}/api`, headers: { Authorization: `Bearer ${token}` } });
};

const StatCard = ({ icon: Icon, label, value, sub, color, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="bg-[#1e293b] border border-slate-700 p-5"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-slate-400 text-xs uppercase tracking-wider">{label}</p>
        <p className={`text-3xl font-bold mt-1 ${color || 'text-white'}`}>{value}</p>
        {sub && <p className="text-slate-500 text-xs mt-1">{sub}</p>}
      </div>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color ? 'bg-opacity-10' : 'bg-slate-800'}`}
        style={{ backgroundColor: color ? undefined : undefined }}>
        <Icon className={`w-5 h-5 ${color || 'text-slate-400'}`} />
      </div>
    </div>
  </motion.div>
);

const SuperAdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await sadApi().get('/sad/dashboard');
        setData(res.data);
      } catch (e) {
        console.error('Dashboard fetch failed:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </SuperAdminLayout>
    );
  }

  if (!data) {
    return (
      <SuperAdminLayout>
        <div className="text-center text-slate-400 py-20">Failed to load dashboard data.</div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <div className="space-y-6" data-testid="sad-dashboard">
        <div>
          <h1 className="text-2xl font-bold text-white">Platform Overview</h1>
          <p className="text-slate-400 text-sm">VendFactura administration console</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Total Users" value={data.users.total}
            sub={`+${data.users.recent_30d} last 30d`} color="text-blue-400" delay={0} />
          <StatCard icon={Store} label="Active Shops" value={data.shops.total}
            sub={`+${data.shops.recent_30d} last 30d`} color="text-emerald-400" delay={0.05} />
          <StatCard icon={FileText} label="Total Invoices" value={data.invoices.total}
            sub={`${data.invoices.paid} paid`} color="text-purple-400" delay={0.1} />
          <StatCard icon={ShoppingCart} label="POS Sales" value={data.sales.total}
            sub={`${data.sales.revenue.toLocaleString('es-ES', { minimumFractionDigits: 2 })} EUR`} color="text-amber-400" delay={0.15} />
        </div>

        {/* Revenue Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#1e293b] border border-slate-700 p-5"
          >
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-4 h-4 text-amber-400" />
              <p className="text-xs text-slate-400 uppercase tracking-wider">Monthly Subscriptions</p>
            </div>
            <p className="text-3xl font-bold text-amber-400">
              {data.subscription_revenue.toLocaleString('es-ES', { minimumFractionDigits: 2 })} EUR
            </p>
            <div className="mt-3 space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-400">
                <span>POS Plan ({data.users.pos})</span>
                <span>{(data.users.pos * 19.99).toFixed(2)} EUR</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Invoice Plan ({data.users.invoice})</span>
                <span>{(data.users.invoice * 4.99).toFixed(2)} EUR</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-[#1e293b] border border-slate-700 p-5"
          >
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <p className="text-xs text-slate-400 uppercase tracking-wider">Invoice Revenue</p>
            </div>
            <p className="text-3xl font-bold text-emerald-400">
              {data.invoices.paid_revenue.toLocaleString('es-ES', { minimumFractionDigits: 2 })} EUR
            </p>
            <p className="text-slate-500 text-sm mt-1">{data.invoices.paid} paid invoices</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-[#1e293b] border border-slate-700 p-5"
          >
            <div className="flex items-center gap-2 mb-3">
              <ArrowUpRight className="w-4 h-4 text-purple-400" />
              <p className="text-xs text-slate-400 uppercase tracking-wider">POS Sales Revenue</p>
            </div>
            <p className="text-3xl font-bold text-purple-400">
              {data.sales.revenue.toLocaleString('es-ES', { minimumFractionDigits: 2 })} EUR
            </p>
            <p className="text-slate-500 text-sm mt-1">{data.sales.total} total sales</p>
          </motion.div>
        </div>

        {/* Subscription Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-[#1e293b] border border-slate-700 p-5"
        >
          <h3 className="text-white font-semibold mb-4">User Distribution</h3>
          <div className="flex gap-6">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">POS Users</span>
                <span className="text-white font-semibold">{data.users.pos}</span>
              </div>
              <div className="w-full bg-slate-700 h-2 rounded-full overflow-hidden">
                <div
                  className="bg-amber-500 h-full rounded-full transition-all"
                  style={{ width: `${data.users.total ? (data.users.pos / data.users.total) * 100 : 0}%` }}
                />
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Invoice Users</span>
                <span className="text-white font-semibold">{data.users.invoice}</span>
              </div>
              <div className="w-full bg-slate-700 h-2 rounded-full overflow-hidden">
                <div
                  className="bg-purple-500 h-full rounded-full transition-all"
                  style={{ width: `${data.users.total ? (data.users.invoice / data.users.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminDashboard;
